// ====================================================================================================
// CarrierSubmitPage.jsx
// ----------------------------------------------------------------------------------------------------
// Carrier-side bid submission page for Tenders and Tender Phases.
//
// This component supports two hosting models:
//
// 1) Legacy tab mode:
//    - TenderConfigCrud writes CarrierBidContext and CarrierBidTender into M_State.
//    - The tab system opens this component as an independent route.
//    - This component reads the bid context from M_State.
//
// 2) Wrapper mode:
//    - CarrierInviteViewBidPage keeps TenderConfigCrud and CarrierSubmitPage inside the same tab.
//    - The wrapper passes bidContext, bidHeader and onBack directly as props.
//    - This component uses those props before falling back to M_State.
//
// Main responsibilities:
// - Resolve the current carrier.
// - Resolve Tender / TenderPhase bid context.
// - Load available lanes for the current carrier and Tender scope.
// - Load existing quotes for edit/view mode.
// - Load accepted quote loads to compute remaining lane capacity.
// - Configure create/update/batch quote services for InviteTableSection.
// - Pass Tender currency symbol into InviteTableSection so Bid Value inputs can show the suffix.
// - Refresh the full carrier bid dataset when the user clicks Refresh.
// ====================================================================================================

import React, {
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
} from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { LoadingContext, MHeader, MButton } from "src/M_Components";
import { BufferService } from "src/M_Components/M_Utils/buffer.service";
import InviteTableSection from "./InviteTableSection";
import { isFailurePayload, apiMessage } from "src/components/utils/apiResponse";
import "./CarrierSubmitPage.css";

//region M_State helpers

/**
 * Reads a value from the global M_State collection by key.
 *
 * Purpose in this file:
 * - provides backward compatibility with the legacy tab flow;
 * - allows this page to resolve CarrierBidContext and CarrierBidTender when
 *   they are not injected by a wrapper component.
 *
 * @param {Array<{key:string,value:any}>} M_State - Redux key-value state collection.
 * @param {string} key - State key to read.
 * @returns {any|null} Stored value, or null when the key is not present.
 */
const pickFromMState = (M_State, key) =>
  M_State.find((o) => o.key === key)?.value ?? null;

//endregion

//region Carrier resolution helpers

/**
 * Resolves the current CarrierId used by the carrier bidding flow.
 *
 * Resolution order:
 * 1) M_State["CarrierId"]
 * 2) LoadingContext.carrierId
 * 3) localStorage "app.auth.carrier.session"
 *
 * Purpose in this file:
 * - keeps the carrier bid page compatible with both older internal tab flows
 *   and newer carrier-auth flows;
 * - prevents editable table actions when the carrier cannot be resolved.
 *
 * @param {Array<{key:string,value:any}>} M_State - Redux key-value state collection.
 * @param {object} ctx - LoadingContext value.
 * @returns {number|null} Current carrier id or null when unavailable.
 */
const useCarrierId = (M_State, ctx) => {
  const carrierIdFromState = useMemo(
    () => M_State.find((o) => o.key === "CarrierId")?.value ?? null,
    [M_State]
  );

  const carrierSession = useMemo(() => {
    try {
      const raw = localStorage.getItem("app.auth.carrier.session");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }, []);

  const carrierId =
    carrierIdFromState ?? ctx.carrierId ?? carrierSession?.CarrierId ?? null;

  return carrierId != null ? Number(carrierId) : null;
};

//endregion

//region API paging helpers

/**
 * Creates a reusable paged loader for BufferService list endpoints.
 *
 * Purpose in this file:
 * - loads all pages for TenderQuotes queries;
 * - normalizes common API response envelopes into a single flat array;
 * - prevents duplicate entries when the endpoint returns repeated ids.
 *
 * Supported response shapes:
 * - { Items: [...] }
 * - { items: [...] }
 * - { Data: [...] }
 * - { data: [...] }
 * - [...]
 *
 * @returns {Function} Async function that loads all pages for a service and filter.
 */
const useLoadAllPages = () => {
  /**
   * Loads all pages for a given service/filter pair.
   *
   * @param {BufferService} svc - Service instance with a filter method.
   * @param {string} filter - OData-style filter string.
   * @returns {Promise<Array>} Flattened list of loaded items.
   */
  const loadAllPages = useCallback(async (svc, filter) => {
    const pageSize = 200;
    let page = 1;
    const acc = [];
    const seen = new Set();

    while (true) {
      const qs = `?Page=${page}&PageSize=${pageSize}&filter=${encodeURIComponent(
        filter
      )}`;

      const res = await svc.filter(qs);

      if (isFailurePayload(res)) {
        break;
      }

      const items = Array.isArray(res?.Items)
        ? res.Items
        : Array.isArray(res?.items)
          ? res.items
          : Array.isArray(res?.Data)
            ? res.Data
            : Array.isArray(res?.data)
              ? res.data
              : Array.isArray(res)
                ? res
                : [];

      if (!items.length) {
        break;
      }

      for (const it of items) {
        const id = it?.Id ?? it?.id;

        if (id != null && !seen.has(id)) {
          seen.add(id);
          acc.push(it);
        }
      }

      const totalPages =
        Number(
          res?.TotalPages ??
          res?.totalPages ??
          res?.Total ??
          res?.total ??
          (items.length < pageSize ? page : page + 1)
        ) || page;

      if (page >= totalPages) {
        break;
      }

      page += 1;
    }

    return acc;
  }, []);

  return loadAllPages;
};

//endregion

//region Lane mapping helpers

/**
 * Maps a flat TenderLane API row into the lane shape expected by InviteTableSection.
 *
 * Purpose in this file:
 * - keeps the bid grid compatible with its existing lane contract;
 * - standardizes lane id and origin/destination names regardless of API casing;
 * - derives an active flag from inactive flags when available.
 *
 * @param {object} it - Flat lane row returned by TenderLanes/flat endpoints.
 * @returns {object} Normalized lane row for the carrier bid grid.
 */
const mapFlatLaneToGridLane = (it) => {
  const laneId = it?.laneId ?? it?.LaneId ?? it?.id ?? it?.Id ?? null;

  const origin = String(it?.sourceLocation ?? it?.SourceLocation ?? "").trim();

  const destination = String(
    it?.destinationLocation ?? it?.DestinationLocation ?? ""
  ).trim();

  return {
    ...it,

    LaneId: laneId != null ? Number(laneId) : null,

    Origin: origin,
    Destination: destination,
    LaneSource: origin,
    LaneDestination: destination,

    IsActive:
      it?.inactive != null
        ? !it.inactive
        : it?.Inactive != null
          ? !it.Inactive
          : true,
  };
};

//endregion

//region Currency helpers

/**
 * Normalizes a currency candidate into a printable symbol.
 *
 * Purpose in this file:
 * - supports both string and object currency payloads;
 * - avoids rendering "[object Object]" when Currency is an object;
 * - allows the Bid Value input to show values such as "$", "€", "USD" or "EUR".
 *
 * @param {any} candidate - Currency value or currency-like object.
 * @returns {string} Printable currency symbol/code, or an empty string.
 */
const normalizeCurrencyCandidate = (candidate) => {
  if (candidate == null) return "";

  if (typeof candidate === "string" || typeof candidate === "number") {
    return String(candidate).trim();
  }

  if (typeof candidate === "object") {
    const value =
      candidate.Symbol ??
      candidate.symbol ??
      candidate.Code ??
      candidate.code ??
      candidate.Name ??
      candidate.name ??
      "";

    return String(value ?? "").trim();
  }

  return "";
};

/**
 * Resolves the currency symbol from all known bid-context shapes.
 *
 * Purpose in this file:
 * - makes the carrier bid grid independent from one exact DTO shape;
 * - supports Spot and TenderPhase contexts;
 * - supports direct currency values, nested Tender currency values and bid header fallbacks.
 *
 * @param {object|null} bidContext - Current bid context.
 * @param {object|null} bidHeader - Header metadata associated with the bid context.
 * @param {object|null} tender - Local Tender DTO.
 * @param {object|null} tenderPhase - Local TenderPhase DTO.
 * @returns {string} Printable currency symbol/code, or an empty string.
 */
const resolveCurrencySymbol = ({ bidContext, bidHeader, tender, tenderPhase }) => {
  const candidates = [
    bidContext?.currencySymbol,
    bidContext?.CurrencySymbol,
    bidContext?.currency,
    bidContext?.Currency,

    bidHeader?.currencySymbol,
    bidHeader?.CurrencySymbol,
    bidHeader?.currency,
    bidHeader?.Currency,

    bidContext?.tenderDto?.currencySymbol,
    bidContext?.tenderDto?.CurrencySymbol,
    bidContext?.tenderDto?.currency,
    bidContext?.tenderDto?.Currency,

    bidContext?.tenderPhaseDto?.Tender?.currency,
    bidContext?.tenderPhaseDto?.Tender?.Currency,
    bidContext?.tenderPhaseDto?.tender?.currency,
    bidContext?.tenderPhaseDto?.tender?.Currency,

    tender?.currencySymbol,
    tender?.CurrencySymbol,
    tender?.currency,
    tender?.Currency,

    tenderPhase?.Tender?.currency,
    tenderPhase?.Tender?.Currency,
    tenderPhase?.tender?.currency,
    tenderPhase?.tender?.Currency,
  ];

  for (const candidate of candidates) {
    const normalized = normalizeCurrencyCandidate(candidate);

    if (normalized) {
      return normalized;
    }
  }

  return "";
};

//endregion

//region Component

/**
 * Renders the carrier bid submission page.
 *
 * Purpose in this file:
 * - coordinates quote submission services and lane data loading;
 * - delegates the editable grid itself to InviteTableSection;
 * - keeps the outer page compatible with both tab-based and wrapper-based flows.
 *
 * @param {object} props - Component props.
 * @param {Function} props.handleTabClose - Optional tab close callback.
 * @param {Function} props.toastMessages - Optional toast dispatcher override.
 * @param {object|null} props.bidContext - Optional direct bid context from wrapper mode.
 * @param {object|null} props.bidHeader - Optional direct bid header from wrapper mode.
 * @param {Function} props.onBack - Optional wrapper back callback.
 * @returns {JSX.Element} Carrier bid submission page.
 */
export default function CarrierSubmitPage({
  handleTabClose,
  toastMessages,
  bidContext: bidContextProp,
  bidHeader: bidHeaderProp,
  onBack,
}) {
  const { t } = useTranslation();
  const ctx = useContext(LoadingContext);
  const M_State = useSelector((s) => s.StateKeyValue);

  const carrierId = useCarrierId(M_State, ctx);
  const loadAllPages = useLoadAllPages();

  //endregion

  //region Toast and global loading infrastructure

  const toastRef = useRef(ctx.toastMessages);
  const setLoadRef = useRef(ctx.setIsLoad);
  const loadingCounterRef = useRef(0);

  /**
   * Starts the shared global loading overlay.
   *
   * Purpose in this file:
   * - allows multiple parallel effects to run without causing overlay flicker;
   * - only enables the overlay when the first async operation starts.
   *
   * @returns {void}
   */
  const beginGlobalLoading = useCallback(() => {
    loadingCounterRef.current += 1;

    if (loadingCounterRef.current === 1) {
      setLoadRef.current?.(true);
    }
  }, []);

  /**
   * Ends one shared global loading operation.
   *
   * Purpose in this file:
   * - decrements the local loading counter defensively;
   * - disables the overlay only when every tracked async operation has finished.
   *
   * @returns {void}
   */
  const endGlobalLoading = useCallback(() => {
    loadingCounterRef.current = Math.max(0, loadingCounterRef.current - 1);

    if (loadingCounterRef.current === 0) {
      setLoadRef.current?.(false);
    }
  }, []);

  useEffect(() => {
    toastRef.current = toastMessages || ctx.toastMessages;
  }, [ctx.toastMessages, toastMessages]);

  useEffect(() => {
    setLoadRef.current = ctx.setIsLoad;
  }, [ctx.setIsLoad]);

  useEffect(() => {
    return () => {
      loadingCounterRef.current = 0;
      setLoadRef.current?.(false);
    };
  }, []);

  //endregion

  //region Bid context resolution

  const bidContext =
    bidContextProp || pickFromMState(M_State, "CarrierBidContext");

  const bidHeader =
    bidHeaderProp || pickFromMState(M_State, "CarrierBidTender");

  const scope = (bidContext?.scope || "tender").toLowerCase();
  const crudMode = (bidContext?.crudMode || "create").toLowerCase();

  const tenderId = bidContext?.tenderId ?? null;
  const tenderPhaseId = bidContext?.tenderPhaseId ?? null;

  const mode = scope === "phase" ? "phase" : "spot";

  const isCreateMode = crudMode === "create";
  const isViewMode = crudMode === "view";

  const tableReadOnly = isViewMode || !carrierId;

  const tender = useMemo(() => bidContext?.tenderDto || {}, [bidContext]);

  const tenderPhase = useMemo(
    () => bidContext?.tenderPhaseDto || {},
    [bidContext]
  );

  const currencySymbol = useMemo(
    () =>
      resolveCurrencySymbol({
        bidContext,
        bidHeader,
        tender,
        tenderPhase,
      }),
    [bidContext, bidHeader, tender, tenderPhase]
  );

  const tenderLoads =
    Number(
      bidContext?.tenderDto?.Loads ??
      bidContext?.tenderDto?.loads ??
      bidContext?.tenderPhaseDto?.Tender?.Loads ??
      bidContext?.tenderPhaseDto?.tender?.Loads ??
      bidHeader?.Loads ??
      bidHeader?.loads ??
      tender?.Loads ??
      tender?.loads ??
      0
    ) || 0;

  //endregion

  //region Services

  const baseUrl = ctx.DEFAULTURL;

  const tenderQuotesService = useMemo(
    () => new BufferService("TenderQuotes", baseUrl),
    [baseUrl]
  );

  const tenderQuotesBatchService = useMemo(
    () => new BufferService("TenderQuotes/create/batch", baseUrl),
    [baseUrl]
  );

  const tenderQuoteCreateService = useMemo(
    () => new BufferService("TenderQuotes/create", baseUrl),
    [baseUrl]
  );

  const tenderQuoteUpdateService = useMemo(
    () => new BufferService("TenderQuotes/update", baseUrl),
    [baseUrl]
  );

  const tenderOptionsService = useMemo(
    () => new BufferService("Tenders", baseUrl),
    [baseUrl]
  );

  const tenderPhaseOptionsService = useMemo(
    () => new BufferService("TenderPhases", baseUrl),
    [baseUrl]
  );

  const tenderLanesFlatService = useMemo(
    () => new BufferService("TenderLanes/flat", baseUrl),
    [baseUrl]
  );

  const tenderLanesCarrierFlatService = useMemo(
    () =>
      new BufferService(`TenderLanes/flat/carrier/${carrierId ?? 0}`, baseUrl),
    [baseUrl, carrierId]
  );

  //endregion

  //region Local state

  const [, setTenderSchemaOptions] = useState([]);
  const [, setPhaseSchemaOptions] = useState([]);
  const [, setSchemasLoading] = useState(false);

  const [lanes, setLanes] = useState(() => bidContext?.lanes || []);
  const [prefillQuotes, setPrefillQuotes] = useState([]);
  const [, setQuotesLoading] = useState(false);

  const [acceptedLoadsByLane, setAcceptedLoadsByLane] = useState({});

  /**
   * Refresh trigger used by data-loading effects.
   *
   * Purpose in this file:
   * - makes the table Refresh button reload lanes and accepted-load capacity,
   *   not only existing quotes;
   * - fixes create mode, where loadQuotes intentionally does not fetch anything.
   */
  const [refreshVersion, setRefreshVersion] = useState(0);

  //endregion

  //region Submit and edit service adapters

  const submitServices = useMemo(
    () => ({
      spotBatch: {
        /**
         * Sends a batch quote submission for a Spot Tender.
         *
         * Purpose in this file:
         * - adapts InviteTableSection item payloads to the TenderQuotes batch API;
         * - injects TenderId and CarrierId from the resolved page context.
         *
         * @param {object} req - Batch request from InviteTableSection.
         * @returns {Promise<any>} API response.
         */
        post: async (req) => {
          const items = Array.isArray(req?.items) ? req.items : [];

          if (!tenderId || !carrierId) {
            return {
              IsFailure: true,
              Message: "Missing tenderId or carrierId for batch submission.",
            };
          }

          const body = {
            tenderId: Number(tenderId),
            carrierId: Number(carrierId),
            items: items.map((it) => ({
              laneId: Number(it.laneId ?? it.LaneId ?? it.laneID ?? 0),
              bidValue: Number(it.bidValue ?? it.BidValue ?? 0),
              loads: Number(it.loads ?? it.Loads ?? 0),
              estimatedTransitDays: Number(
                it.estimatedTransitDays ??
                it.EstimatedTransitDays ??
                it.etd ??
                0
              ),
            })),
          };

          return tenderQuotesBatchService.post(body);
        },
      },

      phaseBatch: {
        /**
         * Sends a batch quote submission for a Tender Phase.
         *
         * Purpose in this file:
         * - adapts InviteTableSection item payloads to the TenderQuotes batch API;
         * - injects TenderPhaseId and CarrierId from the resolved page context;
         * - includes TenderId when available.
         *
         * @param {object} req - Batch request from InviteTableSection.
         * @returns {Promise<any>} API response.
         */
        post: async (req) => {
          const items = Array.isArray(req?.items) ? req.items : [];

          if (!tenderPhaseId || !carrierId) {
            return {
              IsFailure: true,
              Message:
                "Missing tenderPhaseId or carrierId for batch submission.",
            };
          }

          const body = {
            ...(tenderId != null ? { tenderId: Number(tenderId) } : {}),
            tenderPhaseId: Number(tenderPhaseId),
            carrierId: Number(carrierId),
            items: items.map((it) => ({
              laneId: Number(it.laneId ?? it.LaneId ?? it.laneID ?? 0),
              bidValue: Number(it.bidValue ?? it.BidValue ?? 0),
              loads: Number(it.loads ?? it.Loads ?? 0),
              estimatedTransitDays: Number(
                it.estimatedTransitDays ??
                it.EstimatedTransitDays ??
                it.etd ??
                0
              ),
            })),
          };

          return tenderQuotesBatchService.post(body);
        },
      },
    }),
    [carrierId, tenderQuotesBatchService, tenderId, tenderPhaseId]
  );

  const editServices = useMemo(
    () => ({
      spot: {
        /**
         * Creates a single Spot Tender quote.
         *
         * @param {object} payload - Quote create payload.
         * @returns {Promise<any>} API response.
         */
        create: (payload) => tenderQuoteCreateService.post?.(payload),

        /**
         * Updates a single Spot Tender quote and keeps it active.
         *
         * @param {number|string} id - Quote identifier.
         * @param {object} payload - Quote update payload.
         * @returns {Promise<any>} API response.
         */
        update: (id, payload) =>
          tenderQuoteUpdateService.update?.(id, {
            ...payload,
            inactive: false,
          }),
      },

      phase: {
        /**
         * Creates a single Tender Phase quote.
         *
         * @param {object} payload - Quote create payload.
         * @returns {Promise<any>} API response.
         */
        create: (payload) => tenderQuoteCreateService.post?.(payload),

        /**
         * Updates a single Tender Phase quote and keeps it active.
         *
         * @param {number|string} id - Quote identifier.
         * @param {object} payload - Quote update payload.
         * @returns {Promise<any>} API response.
         */
        update: (id, payload) =>
          tenderQuoteUpdateService.update?.(id, {
            ...payload,
            inactive: false,
          }),
      },
    }),
    [tenderQuoteCreateService, tenderQuoteUpdateService]
  );

  //endregion

  //region Schema loading

  useEffect(() => {
    let cancelled = false;

    /**
     * Loads Tender and TenderPhase schema options.
     *
     * Purpose in this file:
     * - keeps metadata available for compatibility with the broader table system;
     * - does not render schema data directly in this page.
     *
     * @returns {Promise<void>}
     */
    const loadSchemas = async () => {
      setSchemasLoading(true);
      beginGlobalLoading();

      try {
        const [tenderOpts, phaseOpts] = await Promise.allSettled([
          tenderOptionsService.options?.(),
          tenderPhaseOptionsService.options?.(),
        ]);

        if (cancelled) return;

        if (
          tenderOpts.status === "fulfilled" &&
          !isFailurePayload(tenderOpts.value)
        ) {
          setTenderSchemaOptions(
            Array.isArray(tenderOpts.value) ? tenderOpts.value : []
          );
        } else {
          setTenderSchemaOptions([]);
        }

        if (
          phaseOpts.status === "fulfilled" &&
          !isFailurePayload(phaseOpts.value)
        ) {
          setPhaseSchemaOptions(
            Array.isArray(phaseOpts.value) ? phaseOpts.value : []
          );
        } else {
          setPhaseSchemaOptions([]);
        }
      } catch (err) {
        if (!cancelled) {
          toastRef.current?.(
            "error",
            "",
            apiMessage(err, t("ErrorLoadingData"))
          );
        }
      } finally {
        endGlobalLoading();

        if (!cancelled) {
          setSchemasLoading(false);
        }
      }
    };

    loadSchemas();

    return () => {
      cancelled = true;
    };
  }, [
    tenderOptionsService,
    tenderPhaseOptionsService,
    t,
    beginGlobalLoading,
    endGlobalLoading,
  ]);

  //endregion

  //region Lane loading

  useEffect(() => {
    let cancelled = false;

    /**
     * Keeps invite-provided lanes when the page cannot load lanes from the API.
     *
     * Purpose in this file:
     * - preserves invite context data during authentication/deep-link flows;
     * - avoids showing stale lanes from another context.
     *
     * @returns {void}
     */
    const keepInviteSeedOrClear = () => {
      if (Array.isArray(bidContext?.lanes) && bidContext.lanes.length) {
        setLanes(bidContext.lanes);
      } else {
        setLanes([]);
      }
    };

    /**
     * Resolves the TenderId from every available context location.
     *
     * Purpose in this file:
     * - supports Spot contexts where tenderId is direct;
     * - supports Phase contexts where the TenderId may be nested in TenderPhase DTOs;
     * - supports wrapper mode and legacy tab mode with the same code path.
     *
     * @returns {number|null} Tender id or null when unavailable.
     */
    const resolveTenderId = () => {
      const fromContext =
        bidContext?.tenderId ??
        bidContext?.tenderDto?.Id ??
        bidContext?.tenderDto?.id ??
        bidContext?.tenderPhaseDto?.TenderId ??
        bidContext?.tenderPhaseDto?.tenderId ??
        bidContext?.tenderPhaseDto?.Tender?.Id ??
        bidContext?.tenderPhaseDto?.Tender?.id ??
        bidContext?.tenderPhaseDto?.tender?.Id ??
        bidContext?.tenderPhaseDto?.tender?.id ??
        null;

      const fromLocal =
        tender?.Id ??
        tender?.id ??
        tenderPhase?.TenderId ??
        tenderPhase?.tenderId ??
        tenderPhase?.Tender?.Id ??
        tenderPhase?.Tender?.id ??
        tenderPhase?.tender?.Id ??
        tenderPhase?.tender?.id ??
        null;

      const finalId = tenderId ?? fromContext ?? fromLocal ?? null;

      return finalId != null ? Number(finalId) : null;
    };

    /**
     * Loads the lane collection for the current carrier bid context.
     *
     * Purpose in this file:
     * - view mode loads all tender lanes for historical inspection;
     * - create/edit mode loads carrier-filtered open lanes from the backend;
     * - the backend remains responsible for eligibility rules, reducing FE filtering;
     * - runs again when refreshVersion changes.
     *
     * @returns {Promise<void>}
     */
    const loadLanes = async () => {
      const resolvedTenderId = resolveTenderId();

      if (!resolvedTenderId) {
        keepInviteSeedOrClear();
        return;
      }

      const svc = isViewMode
        ? tenderLanesFlatService
        : tenderLanesCarrierFlatService;

      if (!carrierId && !isViewMode) {
        keepInviteSeedOrClear();
        return;
      }

      beginGlobalLoading();

      try {
        const pageSize = 200;
        let page = 1;
        const acc = [];
        const seen = new Set();

        const filter = isViewMode
          ? `TenderId eq ${resolvedTenderId}`
          : `TenderId eq ${resolvedTenderId} and IsSatisfied eq false`;

        while (true) {
          if (cancelled) break;

          const res = await svc.filter(
            `?Page=${page}&PageSize=${pageSize}&Filter=${encodeURIComponent(
              filter
            )}`
          );

          if (cancelled) break;
          if (isFailurePayload(res)) break;

          const items = Array.isArray(res?.Items)
            ? res.Items
            : Array.isArray(res?.items)
              ? res.items
              : Array.isArray(res?.Data)
                ? res.Data
                : Array.isArray(res?.data)
                  ? res.data
                  : Array.isArray(res)
                    ? res
                    : [];

          if (!items.length) break;

          for (const it of items) {
            const laneId = it?.laneId ?? it?.LaneId ?? it?.id ?? it?.Id ?? null;

            if (laneId == null || seen.has(laneId)) continue;

            seen.add(laneId);

            const mapped = mapFlatLaneToGridLane(it);

            if (mapped?.LaneId == null) continue;

            acc.push(mapped);
          }

          const totalPages =
            Number(
              res?.TotalPages ??
              res?.totalPages ??
              (items.length < pageSize ? page : page + 1)
            ) || page;

          if (page >= totalPages) break;

          page += 1;
        }

        if (!cancelled) {
          setLanes(acc);
        }
      } catch (err) {
        if (!cancelled) {
          toastRef.current?.(
            "error",
            "",
            apiMessage(err, t("ErrorLoadingData"))
          );
          setLanes([]);
        }
      } finally {
        endGlobalLoading();
      }
    };

    loadLanes();

    return () => {
      cancelled = true;
    };
  }, [
    tenderLanesFlatService,
    tenderLanesCarrierFlatService,
    carrierId,
    tender,
    tenderPhase,
    bidContext,
    t,
    tenderId,
    isViewMode,
    refreshVersion,
    beginGlobalLoading,
    endGlobalLoading,
  ]);

  //endregion

  //region Accepted loads loading

  useEffect(() => {
    let cancelled = false;

    /**
     * Resolves the TenderId used to load accepted quote totals.
     *
     * Purpose in this file:
     * - accepted load totals are Tender-wide;
     * - the same calculation supports Spot and TenderPhase bid pages.
     *
     * @returns {number|null} Tender id or null when unavailable.
     */
    const resolveTenderId = () => {
      const fromContext =
        bidContext?.tenderId ??
        bidContext?.tenderDto?.Id ??
        bidContext?.tenderDto?.id ??
        bidContext?.tenderPhaseDto?.TenderId ??
        bidContext?.tenderPhaseDto?.tenderId ??
        bidContext?.tenderPhaseDto?.Tender?.Id ??
        bidContext?.tenderPhaseDto?.Tender?.id ??
        bidContext?.tenderPhaseDto?.tender?.Id ??
        bidContext?.tenderPhaseDto?.tender?.id ??
        null;

      const finalId = tenderId ?? fromContext ?? null;

      return finalId != null ? Number(finalId) : null;
    };

    /**
     * Loads accepted quotes and aggregates accepted loads by lane.
     *
     * Purpose in this file:
     * - computes remaining capacity per lane for InviteTableSection;
     * - prevents carriers from submitting loads above the remaining lane capacity;
     * - runs again when refreshVersion changes.
     *
     * @returns {Promise<void>}
     */
    const loadAcceptedQuotes = async () => {
      const resolvedTenderId = resolveTenderId();

      if (!resolvedTenderId || !carrierId) {
        setAcceptedLoadsByLane({});
        return;
      }

      beginGlobalLoading();

      try {
        const filter = `TenderId eq ${resolvedTenderId} and IsAccepted eq true and Inactive eq false`;
        const list = await loadAllPages(tenderQuotesService, filter);

        if (cancelled) return;

        const loadsMap = {};

        for (const q of list) {
          const laneId = Number(q?.LaneId ?? q?.laneId ?? 0);

          if (!laneId) continue;

          const loads = Number(q?.Loads ?? q?.loads ?? 0) || 0;
          loadsMap[laneId] = (loadsMap[laneId] || 0) + loads;
        }

        setAcceptedLoadsByLane(loadsMap);
      } catch {
        if (!cancelled) {
          setAcceptedLoadsByLane({});
        }
      } finally {
        endGlobalLoading();
      }
    };

    loadAcceptedQuotes();

    return () => {
      cancelled = true;
    };
  }, [
    bidContext,
    tenderId,
    carrierId,
    tenderQuotesService,
    loadAllPages,
    refreshVersion,
    beginGlobalLoading,
    endGlobalLoading,
  ]);

  //endregion

  //region Existing quote loading

  /**
   * Loads existing carrier quotes for edit and view modes.
   *
   * Purpose in this file:
   * - pre-fills the grid with previously submitted quote values;
   * - scopes Spot quotes by TenderId;
   * - scopes Phase quotes by TenderPhaseId;
   * - keeps create mode empty because new submissions start without prefill.
   *
   * @returns {Promise<void>}
   */
  const loadQuotes = useCallback(async () => {
    if (!carrierId) {
      setPrefillQuotes([]);
      return;
    }

    if (isCreateMode) {
      setPrefillQuotes([]);
      return;
    }

    let filter = null;

    if (mode === "spot" && tenderId) {
      filter = `CarrierId eq ${carrierId} and TenderId eq ${tenderId} and Inactive eq false`;
    } else if (mode === "phase" && tenderPhaseId) {
      filter = `CarrierId eq ${carrierId} and TenderPhaseId eq ${tenderPhaseId} and Inactive eq false`;
    }

    if (!filter) {
      setPrefillQuotes([]);
      return;
    }

    setQuotesLoading(true);
    beginGlobalLoading();

    try {
      const list = await loadAllPages(tenderQuotesService, filter);

      const normalized = list
        .map((q) => {
          const laneId = q?.LaneId ?? q?.laneId;
          const bid = q?.BidValue ?? q?.bidValue;
          const loads = q?.Loads ?? q?.loads;

          const etd =
            q?.EstimatedTransitDays ??
            q?.estimatedTransitDays ??
            q?.Etd ??
            q?.etd ??
            null;

          const quoteId = q?.QuoteId ?? q?.Id ?? q?.id ?? null;

          return {
            LaneId: laneId != null ? Number(laneId) : null,
            BidValue: bid != null ? Number(bid) : null,
            Loads: loads != null ? Number(loads) : null,
            EstimatedTransitDays: etd != null ? Number(etd) : null,
            QuoteId: quoteId != null ? Number(quoteId) : null,
          };
        })
        .filter((x) => x.LaneId != null);

      setPrefillQuotes(normalized);
    } catch (err) {
      toastRef.current?.("error", "", apiMessage(err, t("ErrorLoadingData")));
      setPrefillQuotes([]);
    } finally {
      setQuotesLoading(false);
      endGlobalLoading();
    }
  }, [
    carrierId,
    isCreateMode,
    mode,
    tenderId,
    tenderPhaseId,
    tenderQuotesService,
    loadAllPages,
    t,
    beginGlobalLoading,
    endGlobalLoading,
  ]);

  useEffect(() => {
    loadQuotes();
  }, [loadQuotes]);

  /**
   * Refreshes all visible carrier bid data.
   *
   * Purpose in this file:
   * - reloads existing quotes in edit/view mode;
   * - forces the lane-loading effect to run again;
   * - forces the accepted-load capacity effect to run again;
   * - fixes create mode refresh, where loadQuotes intentionally has no visible work.
   *
   * @returns {Promise<void>}
   */
  const handleRefreshCarrierBidData = async () => {
    setRefreshVersion((value) => value + 1);
    await loadQuotes();
  };

  //endregion

  //region Navigation callbacks

  /**
   * Handles successful batch submission.
   *
   * Purpose in this file:
   * - closes the tab in legacy tab mode;
   * - returns to the configuration view in wrapper mode.
   *
   * @returns {void}
   */
  const handleBatchSubmitSuccess = useCallback(() => {
    if (typeof handleTabClose === "function") {
      handleTabClose();
    } else if (typeof onBack === "function") {
      onBack();
    }
  }, [handleTabClose, onBack]);

  //endregion

  //region Empty context guard

  const hasValidContext =
    !!bidContext &&
    (tenderId != null || (scope === "phase" && tenderPhaseId != null));

  if (!hasValidContext) {
    return (
      <div
        style={{
          padding: "20px",
          background: "var(--color-white)",
          minHeight: "200px",
        }}
      >
        <h2 style={{ marginBottom: "10px" }}>
          {t("CarrierBid") || "Carrier Bid"}
        </h2>

        <p>{t("No bid context was found for this tab.")}</p>
      </div>
    );
  }

  //endregion

  //region Render

  return (
    <>
      <MHeader
        element={
          <>
            {onBack && (
              <MButton
                tooltip={t("Back") || "Back"}
                icon={<i className="pi pi-arrow-left iconTable" />}
                onClick={onBack}
                label={t("Back") || "Back"}
                labelColor={"var(--color-red)"}
              />
            )}

            {!isCreateMode && (
              <MButton
                tooltip={t("RefreshTable")}
                icon={<i className="pi pi-refresh iconTable" />}
                onClick={handleRefreshCarrierBidData}
                label={t("RefreshTable")}
              />
            )}
          </>
        }
      />

      <div
        style={{
          marginBottom: "10px",
          padding: "20px",
          background: "var(--color-white)",
        }}
      >
        <div className="firstDivContainer">
          <InviteTableSection
            token={null}
            mode={mode}
            lanes={lanes || []}
            onRefresh={handleRefreshCarrierBidData}
            submitServices={submitServices}
            editServices={editServices}
            crudMode={crudMode}
            readOnly={tableReadOnly}
            prefillQuotes={prefillQuotes}
            tenderId={tenderId}
            tenderPhaseId={tenderPhaseId}
            carrierId={carrierId}
            tenderLoads={tenderLoads}
            acceptedLoadsByLane={acceptedLoadsByLane}
            currencySymbol={currencySymbol}
            onBatchSubmitSuccess={handleBatchSubmitSuccess}
          />
        </div>
      </div>
    </>
  );

  //endregion
}

//endregion

//region PropTypes

CarrierSubmitPage.propTypes = {
  handleTabClose: PropTypes.func,
  toastMessages: PropTypes.func,

  bidContext: PropTypes.shape({
    scope: PropTypes.oneOf(["tender", "phase"]),
    crudMode: PropTypes.oneOf(["create", "edit", "view"]),
    tenderId: PropTypes.number,
    tenderPhaseId: PropTypes.number,
    tenderDto: PropTypes.object,
    tenderPhaseDto: PropTypes.object,
    lanes: PropTypes.array,
    currencySymbol: PropTypes.string,
    CurrencySymbol: PropTypes.string,
    currency: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    Currency: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  }),

  bidHeader: PropTypes.shape({
    Id: PropTypes.number,
    Name: PropTypes.string,
    Loads: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    loads: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    currencySymbol: PropTypes.string,
    CurrencySymbol: PropTypes.string,
    currency: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    Currency: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  }),

  onBack: PropTypes.func,
};

//endregion